/**
 * OrganizationAutoServiceSettings
 * Inställningar för automatisk skapning av däckbyteservice.
 * Visas på toppnod i OrganizationsManagement.
 * Standardvärden: Vinterdäck 1 dec, Sommardäck 1 apr, 60 dagar i förväg.
 */

import React, { useState } from 'react';
import { useTranslation } from '@/store/languageStore';

const MONTHS = [
  { value: 1, label: 'Januari' },
  { value: 2, label: 'Februari' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'April' },
  { value: 5, label: 'Maj' },
  { value: 6, label: 'Juni' },
  { value: 7, label: 'Juli' },
  { value: 8, label: 'Augusti' },
  { value: 9, label: 'September' },
  { value: 10, label: 'Oktober' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

interface AutoServiceData {
  auto_tire_service_enabled?: boolean;
  winter_tire_month?: number;
  winter_tire_day?: number;
  summer_tire_month?: number;
  summer_tire_day?: number;
  tire_service_advance_days?: number;
}

interface Props {
  data: AutoServiceData;
  onChange: (updates: AutoServiceData) => void;
}

const OrganizationAutoServiceSettings: React.FC<Props> = ({ data, onChange }) => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(data.auto_tire_service_enabled ?? false);
  const [winterMonth, setWinterMonth] = useState(data.winter_tire_month ?? 12);
  const [winterDay, setWinterDay] = useState(data.winter_tire_day ?? 1);
  const [summerMonth, setSummerMonth] = useState(data.summer_tire_month ?? 4);
  const [summerDay, setSummerDay] = useState(data.summer_tire_day ?? 15);
  const [advanceDays, setAdvanceDays] = useState(data.tire_service_advance_days ?? 60);

  const handleChange = (updates: Partial<AutoServiceData>) => {
    const next = {
      auto_tire_service_enabled: enabled,
      winter_tire_month: winterMonth,
      winter_tire_day: winterDay,
      summer_tire_month: summerMonth,
      summer_tire_day: summerDay,
      tire_service_advance_days: advanceDays,
      ...updates,
    };
    onChange(next);
  };

  const toggleEnabled = () => {
    const next = !enabled;
    setEnabled(next);
    handleChange({ auto_tire_service_enabled: next });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          {t('service.autoTire.title')}
        </h3>
        <p className="text-sm text-textTertiary">{t('service.autoTire.description')}</p>
      </div>

      {/* Enable toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
        <div>
          <div className="font-medium text-gray-800">{t('service.autoTire.enable')}</div>
          <div className="text-sm text-textTertiary">{t('service.autoTire.enableHint')}</div>
        </div>
        <button
          onClick={toggleEnabled}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-primary' : 'bg-gray-300'}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {/* Settings (only when enabled) */}
      <div className={`space-y-5 transition-opacity ${enabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
        {/* Winter tires deadline */}
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <h4 className="font-medium text-blue-900 mb-3">🌨 {t('service.autoTire.winterDeadline')}</h4>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs text-blue-700 mb-1">{t('common.day')}</label>
              <input
                type="number"
                min={1}
                max={31}
                value={winterDay}
                onChange={e => { const v = parseInt(e.target.value); setWinterDay(v); handleChange({ winter_tire_day: v }); }}
                className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white"
              />
            </div>
            <div className="flex-2 min-w-[140px]">
              <label className="block text-xs text-blue-700 mb-1">{t('common.month')}</label>
              <select
                value={winterMonth}
                onChange={e => { const v = parseInt(e.target.value); setWinterMonth(v); handleChange({ winter_tire_month: v }); }}
                className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm bg-white"
              >
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs text-blue-600 mt-2">
            {t('service.autoTire.winterHint')} ({winterDay} {MONTHS.find(m => m.value === winterMonth)?.label})
          </p>
        </div>

        {/* Summer tires allowed */}
        <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4">
          <h4 className="font-medium text-yellow-900 mb-3">☀️ {t('service.autoTire.summerDeadline')}</h4>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs text-yellow-700 mb-1">{t('common.day')}</label>
              <input
                type="number"
                min={1}
                max={31}
                value={summerDay}
                onChange={e => { const v = parseInt(e.target.value); setSummerDay(v); handleChange({ summer_tire_day: v }); }}
                className="w-full border border-yellow-200 rounded-lg px-3 py-2 text-sm bg-white"
              />
            </div>
            <div className="flex-2 min-w-[140px]">
              <label className="block text-xs text-yellow-700 mb-1">{t('common.month')}</label>
              <select
                value={summerMonth}
                onChange={e => { const v = parseInt(e.target.value); setSummerMonth(v); handleChange({ summer_tire_month: v }); }}
                className="w-full border border-yellow-200 rounded-lg px-3 py-2 text-sm bg-white"
              >
                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs text-yellow-600 mt-2">
            {t('service.autoTire.summerHint')} ({summerDay} {MONTHS.find(m => m.value === summerMonth)?.label})
          </p>
        </div>

        {/* Advance days */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('service.autoTire.advanceDays')}
          </label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={7}
              max={180}
              value={advanceDays}
              onChange={e => { const v = parseInt(e.target.value); setAdvanceDays(v); handleChange({ tire_service_advance_days: v }); }}
              className="border rounded-lg px-3 py-2 text-sm w-24"
            />
            <span className="text-sm text-textTertiary">{t('service.autoTire.advanceDaysUnit')}</span>
          </div>
          <p className="text-xs text-textTertiary mt-1">{t('service.autoTire.advanceDaysHint')}</p>
        </div>
      </div>
    </div>
  );
};

export default OrganizationAutoServiceSettings;
